import { MessageDescriptor } from "react-intl";
import { useState, useMemo, useCallback, useEffect } from "react";

import { isEqual } from "../../utils";
import { validateDraft, getInitialDraftState } from "./SubscriptionEditProvider.utils";

import { useUpdateSubscriptionMutation, useAddSubscriptionMutation, useDeleteSubscriptionMutation } from "../../api";
import { SubscriptionItem, SubscriptionItemDraft, SubscriptionItemPayload } from "../../types";
import type {
  ISubscriptionEditProviderContextValue,
  ISubscriptionEditProviderContextState,
  ISubscriptionEditProviderContextActions,
  ISubscriptionEditProviderProps,
} from "./SubscriptionEditProvider.types";

export function useSubscriptionEditState(props: ISubscriptionEditProviderProps): ISubscriptionEditProviderContextValue {
  const { mutate: updateSubscriptionMutation } = useUpdateSubscriptionMutation();
  const { mutate: addSubscriptionMutation } = useAddSubscriptionMutation();
  const { mutate: deleteSubscriptionMutation } = useDeleteSubscriptionMutation();

  const [applyPayloadFunction, setApplyPayloadFunction] = useState<(payload: SubscriptionItemPayload) => void | undefined>();
  const [currentSubscriptionItem, setCurrentSubscriptionItem] = useState<SubscriptionItemDraft>(() => getInitialDraftState(props));

  const [validationErrors, setValidationErrors] = useState<Partial<Record<keyof SubscriptionItemDraft, MessageDescriptor>> | null>(null);
  const savedSubscriptionItem = useMemo<SubscriptionItem | undefined>(() => {
    if ("subscriptionItem" in props) {
      return props.subscriptionItem;
    }
    return undefined;
  }, [props]);
  const addSubscription = useCallback(() => {
    if (!validationErrors) {
      addSubscriptionMutation(currentSubscriptionItem);
    }
  }, [addSubscriptionMutation, currentSubscriptionItem, validationErrors]);

  const deleteSubscription = useCallback(() => {
    if (savedSubscriptionItem) {
      deleteSubscriptionMutation(savedSubscriptionItem);
    }
  }, [deleteSubscriptionMutation, savedSubscriptionItem]);

  const updateSubscription = useCallback(() => {
    if (savedSubscriptionItem && !validationErrors) {
      updateSubscriptionMutation({ oldSubscriptionItem: savedSubscriptionItem, newSubscriptionItem: currentSubscriptionItem });
    }
  }, [updateSubscriptionMutation, savedSubscriptionItem, currentSubscriptionItem, validationErrors]);

  const cancelSubscriptionChanges = useCallback(() => {
    setCurrentSubscriptionItem(getInitialDraftState(props));

    if (applyPayloadFunction) {
      applyPayloadFunction(getInitialDraftState(props).payload);
    }
  }, [props, applyPayloadFunction]);

  const setSubscriptionPropertyValue = useCallback(
    <K extends keyof SubscriptionItemDraft>(key: K) =>
      (value: SubscriptionItemDraft[K]) => {
        setCurrentSubscriptionItem((prev) => ({
          ...prev,
          [key]: value,
        }));
      },
    []
  );

  useEffect(() => {
    setValidationErrors(validateDraft(currentSubscriptionItem));
  }, [currentSubscriptionItem]);

  const isSubscriptionNotChange = isEqual(savedSubscriptionItem, currentSubscriptionItem);
  const state = useMemo<ISubscriptionEditProviderContextState>(
    () => ({
      currentSubscriptionItem,
      savedSubscriptionItem,
      validationErrors,
      isUpdateDisabled: !savedSubscriptionItem || !!validationErrors || isSubscriptionNotChange,
      isAddDisabled: !!savedSubscriptionItem || !!validationErrors,
      isDeleteDisabled: !savedSubscriptionItem,
      isCancelChangesDisabled: !savedSubscriptionItem || isSubscriptionNotChange,
    }),
    [savedSubscriptionItem, currentSubscriptionItem, validationErrors, isSubscriptionNotChange]
  );
  const actions = useMemo<ISubscriptionEditProviderContextActions>(
    () => ({
      addSubscription,
      deleteSubscription,
      updateSubscription,
      cancelSubscriptionChanges,
      setSubscriptionPropertyValue,
      setApplyPayloadFunction: (func) => setApplyPayloadFunction(() => func),
    }),
    [
      addSubscription,
      deleteSubscription,
      updateSubscription,
      cancelSubscriptionChanges,
      setSubscriptionPropertyValue,
      setApplyPayloadFunction,
    ]
  );

  const value = useMemo(() => ({ state, actions }), [actions, state]);

  return value;
}
